import { Button } from "@/components/ui/Button";
import { Menu } from "@/components/ui/Menu";
import { DownloadIcon } from "@/components/icons";

/**
 * PDF, HTML and Markdown export (PRD FR-9.1, FR-9.2, HTML-1).
 *
 * Plain links, not fetches. The endpoints answer with a `Content-Disposition`
 * attachment, so the browser downloads them with the session cookie it already
 * has — and a PDF that takes twenty seconds to render is the browser's problem
 * to show progress for, not a spinner this component has to fake.
 *
 * "with SQL" is a separate item rather than a checkbox because it is a separate
 * document: FR-9.1 makes the "Queries used" appendix optional, and a report
 * circulated outside the team usually should not carry it.
 */
export function ExportMenu({
  base,
  label = "Export",
  html = false,
}: {
  /** `/api/reports/{id}` — the file endpoints hang off it. */
  base: string;
  label?: string;
  /** Offer the self-contained HTML file — reports and dashboards (HTML-1). */
  html?: boolean;
}) {
  const items: Array<[string, string]> = [
    ["PDF", `${base}/export.pdf`],
    ["PDF with SQL appendix", `${base}/export.pdf?sql=1`],
    ...(html
      ? ([
          ["Interactive HTML", `${base}/export.html`],
          ["Interactive HTML with SQL appendix", `${base}/export.html?sql=1`],
        ] as Array<[string, string]>)
      : []),
    ["Markdown", `${base}/export.md`],
    ["Markdown with SQL appendix", `${base}/export.md?sql=1`],
  ];

  return (
    <Menu
      label={label}
      className="min-w-56"
      trigger={(props) => (
        <Button icon={<DownloadIcon className="size-4" />} {...props}>
          {label}
        </Button>
      )}
    >
      {(close) =>
        items.map(([text, href]) => (
          <a
            key={href}
            href={href}
            role="menuitem"
            onClick={close}
            className="type-support text-ink hover:bg-hover rounded-inner motion-fast px-2 py-1.5"
          >
            {text}
          </a>
        ))
      }
    </Menu>
  );
}
